/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import React from "react";
import Box from "@mui/material/Box";
import {ErrorMessage, useField, useFormikContext} from "formik";
import BooleanFieldSwitch from "qqq/components/forms/BooleanFieldSwitch";
import {useRowEditContext} from "../RowEditContext";

interface Props
{
   fieldName: string;
   isEditable: boolean;
}

export const BooleanEditCell: React.FC<Props> = ({fieldName, isEditable}) =>
{
   const {values} = useFormikContext<any>();
   const [, meta] = useField(fieldName);
   const showError = meta.touched && !!meta.error;
   const triggerAdjuster = useRowEditContext()?.handleFieldBlur;
   /////////////////////////////////////////////////////////////////////////////////
   // BooleanFieldSwitch's inner Stack carries `padding-top: 5px` from a .compactForm //
   // scoped sx rule (sized for RecordScreen).  That offset pushes the switch below  //
   // the text-input tops in the row-edit context, so zero it out here.  The sx &    //
   // selector plus !important beats the emotion-generated Stack class regardless of //
   // whether the base .MuiStack-root class is present.                              //
   /////////////////////////////////////////////////////////////////////////////////
   return (
      <Box sx={{
         width: "100%",
         display: "flex",
         flexDirection: "column",
         "& .MuiStack-root, & > div > .MuiStack-root, & [class*='MuiStack-root']": {
            paddingTop: "0 !important",
         },
      }}>
         <BooleanFieldSwitch
            name={fieldName}
            label=""
            value={values?.[fieldName] ?? null}
            isDisabled={!isEditable}
            onChangeCallback={(newValue) => triggerAdjuster?.(fieldName, newValue)}
         />
         {showError && (
            <Box className="qqq-row-edit-field-error" sx={{fontSize: "0.75rem", color: "error.main", mt: "2px", mb: "2px", lineHeight: 1.2, whiteSpace: "normal", wordBreak: "break-word"}}>
               <ErrorMessage name={fieldName} render={msg => <span data-field-error="true">{msg}</span>} />
            </Box>
         )}
      </Box>
   );
};
